import { MouseEvent, useCallback } from 'react';

import {
  createPath,
  resolvePath,
  NavigateOptions as NavigateOptionsFromReactRouter,
  To,
  useLocation,
  useNavigate as useNavigateFromReactRouter,
} from 'react-router-dom';

import { addOrgIdToLocation } from './utils';
import { GetLocationDescriptorSettings } from '@cast/utils';

import { useOrganizations } from '@cast/shared-organization-context-data-access';

type NavigationOptions = NavigateOptionsFromReactRouter & {
  descriptor?: GetLocationDescriptorSettings;
  /**
   * Pass this when you need A11y support when invoking `navigate()` via `onRowClick` handler
   * in the table. Otherwise, always use `<Button component={Link} variant="text">`.
   */
  clickEvent?: MouseEvent<Element>;
  /**
   * This option was added to aid transition from react-router-dom useNavigate
   * which does not add an `org` search param if it's missing in the current URL.
   */
  doNotForwardOrganizationId?: boolean;
};

/**
 * NOTE: use `<Button>` with `component={Link}` to create SPA links in the app. Imperative navigation
 * should be done only when JSX doesn't support using native <Link>s and/or for navigation as a side effect.
 *
 * Returns an imperative method for changing the location. Used by `<Link>`s, but may also be used by other elements to change the location.
 * During navigation it appends `org={current-org-id}` as a search param.
 * @see https://reactrouter.com/hooks/use-navigate
 */
export const useNavigate = () => {
  const navigate = useNavigateFromReactRouter();
  const { currentOrganization } = useOrganizations();
  const location = useLocation();

  return useCallback(
    (to: To, options?: NavigationOptions) => {
      const {
        clickEvent: event,
        doNotForwardOrganizationId,
        ...routerOptions
      } = options || {};

      const orgId = doNotForwardOrganizationId
        ? undefined
        : currentOrganization?.id;

      // Mimics browser behaviour when holding the ctrl/cmd key
      const openInNewTab =
        event?.type === 'click' && (event?.metaKey || event?.ctrlKey);

      const destination =
        addOrgIdToLocation(orgId, {
          path: to,
          location,
          locationSettings: routerOptions?.descriptor,
          openInNewTab,
        }) ?? to;

      if (openInNewTab) {
        event.preventDefault();

        const pathString = createPath(
          resolvePath(destination, location.pathname)
        );
        const fullUrl = new URL(pathString, window.location.origin).href;

        window.open(fullUrl, '_blank', 'noopener,noreferrer');
      } else {
        navigate(destination, routerOptions);
      }
    },
    [navigate, currentOrganization?.id, location]
  );
};

export const useNavigateBack = () => {
  const navigate = useNavigateFromReactRouter();

  return useCallback(() => navigate(-1), [navigate]);
};
